// src/pages/patientPages/RecordType/RecordType.jsx
"use client";

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { message, Spin, Alert } from "antd";

import AppLayout from "../../../navigation/Layout/Layout";
import ScrollTabs from "../../../Profile/components/ScrollTabs/ScrollTabs";
import RecordListPanel from "../../components/RecordListPanel/RecordListPanel";
import RecordViewerPanel from "../../components/RecordViewerPanel/RecordViewerPanel";

import useAuthSession from "../../../authPatient/hooks/useAuthSession";
import useExtraction from "../../hooks/useExtraction";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";
import { fetchDocumentsByType } from "../../redux/thunks/extractionThunks";
import { resetDocumentsByType } from "../../redux/slices/extractionSlice";
import { moveDocumentToTrash } from "../../redux/thunks/trashThunks";

import "./RecordType.css";

const RECORD_TYPES = [
  { id: "BILAN_BIOCHIMIQUE", label: "Biochemistry Tests", description: "Blood tests and biochemical analysis results" },
  { id: "IRM", label: "MRI Scans", description: "Magnetic resonance imaging scans" },
  { id: "ECHOGRAPHIE", label: "Medical Ultrasound", description: "Ultrasound examination results" },
  { id: "RADIOGRAPHIE", label: "X-Ray Reports", description: "Radiography and X-ray results" },
  { id: "ORDONNANCE", label: "Prescriptions", description: "Medical prescriptions" },
  { id: "COMPTE_RENDU", label: "Medical Reports", description: "Detailed medical reports" },
  { id: "AUTRE", label: "Other Documents", description: "Other medical documents" },
];

const TYPE_TO_COLLECTION = {
  BILAN_BIOCHIMIQUE: "bilan",
  IRM: "irm",
  ECHOGRAPHIE: "ecographie",
  RADIOGRAPHIE: "rapport",
  ORDONNANCE: "ordonnance",
  COMPTE_RENDU: "compte_rendu",
  AUTRE: "autre",
};

export default function RecordType() {
  const { type } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const {
    loading: authLoading,
    effectiveUserId,
    principalUserId,
    activeSubprofileId,
  } = useAuthSession({ auto: true });
  const userId = effectiveUserId;
  const ownerUserId = activeSubprofileId ? principalUserId : effectiveUserId;
  const scopeSubprofileId = activeSubprofileId || null;

  // ✅ State séparé — jamais pollué par fetchMyDocuments
  const documentsByType        = useSelector((s) => s.extraction?.documentsByType        ?? []);
  const documentsByTypeLoading = useSelector((s) => s.extraction?.documentsByTypeLoading ?? false);

  const { isHealthy } = useExtraction(ownerUserId, {
    subprofileId: scopeSubprofileId,
  });

  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedRecord, setSelectedRecord]     = useState(null);

  const forcedTabKey = ROUTES.MEDICAL_RECORD;
  const activeKey = useMemo(() => pathname || forcedTabKey, [pathname, forcedTabKey]);

  // ✅ Reset au montage pour éviter résidus navigation précédente
  useEffect(() => {
    dispatch(resetDocumentsByType());
  }, [dispatch]);

  // ✅ Résoudre le type depuis les params
  useEffect(() => {
    const record = RECORD_TYPES.find((r) => r.id === String(type || "").toUpperCase());
    if (record) {
      setSelectedRecord(record);
    } else {
      message.error("Invalid document type");
      navigate(ROUTES.MEDICAL_RECORD || "/medicalrecord");
    }
  }, [type, navigate]);

  // ✅ Charger uniquement les docs du bon type — pas de flash
  useEffect(() => {
    if (ownerUserId && selectedRecord && isHealthy) {
      dispatch(
        fetchDocumentsByType({
          type: selectedRecord.id,
          skip: 0,
          limit: 50,
          userId: ownerUserId,
          subprofileId: scopeSubprofileId,
        })
      );
    }
  }, [dispatch, ownerUserId, scopeSubprofileId, selectedRecord, isHealthy]);

  const refreshList = useCallback(() => {
    if (ownerUserId && selectedRecord) {
      dispatch(
        fetchDocumentsByType({
          type: selectedRecord.id,
          skip: 0,
          limit: 50,
          userId: ownerUserId,
          subprofileId: scopeSubprofileId,
        })
      );
    }
  }, [dispatch, ownerUserId, scopeSubprofileId, selectedRecord]);

  const handleDelete = useCallback(async (item) => {
    let docId = null;
    let docData = null;

    if (item?.rawData) {
      docData = item.rawData;
      docId = docData._id || docData.id;
    } else if (item?.id || item?._id) {
      docId = item.id || item._id;
      docData = item;
    } else {
      message.error("Cannot delete: Invalid document");
      return;
    }

    if (!docId) {
      message.error("Cannot delete: Missing document ID");
      return;
    }

    let collection =
      docData?.collection ??
      (docData?.type_document
        ? TYPE_TO_COLLECTION[docData.type_document] || String(docData.type_document).toLowerCase()
        : TYPE_TO_COLLECTION[selectedRecord?.id] || String(selectedRecord?.id || "autre").toLowerCase());

    collection = String(collection).toLowerCase().replace(/-/g, "_").replace(/\s+/g, "_");

    try {
      if (!item?.result) {
        await dispatch(
          moveDocumentToTrash({
            collection,
            documentId: docId,
            userId: ownerUserId,
            version: docData?.version,
          }),
        ).unwrap();
      }
      if (selectedDocument?.id === docId || selectedDocument?._id === docId) {
        setSelectedDocument(null);
      }
      refreshList();
    } catch (error) {
      message.error(error?.message || error || "Error archiving document");
    }
  }, [dispatch, ownerUserId, selectedDocument, selectedRecord, refreshList]);

  const handleDocumentClick = useCallback((doc) => {
    setSelectedDocument(doc);
  }, []);

  const handleDownload = useCallback((doc) => {
    const fileUrl = doc?.chemin_fichier || doc?.file_path;
    if (!fileUrl) return;
    const normalizedPath = String(fileUrl)
      .replace(/\\\\/g, "/")
      .replace(/\\/g, "/")
      .replace(/\/\//g, "/");
    const fullUrl = `https://back.mediwave.tn/api/${normalizedPath}`;
    const link = document.createElement("a");
    link.href = fullUrl;
    link.download = doc?.nom_fichier || "document";
    link.click();
  }, []);

  if (authLoading || !userId || !ownerUserId) {
    return (
      <AppLayout>
        <div style={{ textAlign: "center", padding: 50 }}>
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  if (!selectedRecord) {
    return (
      <AppLayout>
        <Alert message="Invalid document type" type="error" showIcon />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="profile">
        <div className="record-type-page">
          <ScrollTabs
            activeKey={activeKey}
            forceActiveKey={forcedTabKey}
            onChange={(key) => {

              navigate(key);
            }}
            userId={userId}
          />
          <div className="rtp-content">
            <div className="rtp-body">
              <div className="card">
                <RecordListPanel
                  selectedRecord={selectedRecord}
                  items={documentsByType}
                  loading={documentsByTypeLoading}
                  onItemClick={handleDocumentClick}
                  onDelete={handleDelete}
                  onBack={() => navigate(ROUTES.MEDICAL_RECORD || "/medicalrecord")}
                  subjectPatientId={ownerUserId}
                />
              </div>
              <div className="card rtp-viewer">
                <RecordViewerPanel
                  title={selectedRecord.label}
                  selected={selectedDocument}
                  onDelete={handleDelete}
                  patientId={ownerUserId}
                  onShare={() => message.info("Share feature coming soon")}
                  onPrint={() => window.print()}
                  onAnalyseAI={() => message.info("AI Analysis coming soon")}
                  onDownload={handleDownload}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
